import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from '@mlm/ui';
import {
  createSpecialLink,
  deleteSpecialLink,
  listSpecialLinks,
  updateSpecialLink,
  type SpecialLink,
  type SpecialLinkInput,
} from '../lib/api';

/**
 * Admin CRUD for the subscription-gated "special links" (Zoom/Vimeo live). The
 * raw URL is stored server-side and is NEVER returned to members on the list —
 * members open it through a subscription-gated action. Enable the subscription +
 * set its monthly price in Settings.
 */
export function SpecialLinksPage() {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: ['admin-special-links'], queryFn: listSpecialLinks });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-special-links'] });
  const create = useMutation({
    mutationFn: createSpecialLink,
    meta: { successMessage: 'Link created.' },
    onSuccess: () => {
      invalidate();
      setCreating(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<SpecialLinkInput> }) =>
      updateSpecialLink(id, patch),
    meta: { successMessage: 'Link saved.' },
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });
  const remove = useMutation({
    mutationFn: deleteSpecialLink,
    meta: { successMessage: 'Link deleted.' },
    onSuccess: invalidate,
  });

  const links = query.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold">Special links</h1>
        <Button onClick={() => setCreating(true)}>+ New link</Button>
      </div>
      <p className="text-sm text-muted-foreground">
        Zoom / Vimeo live links shown ONLY to members with an active subscription. The URL is never
        exposed on the member list — members open it through a subscription-gated action. Enable the
        subscription and set its monthly price in Settings.
      </p>

      {creating && (
        <SpecialLinkForm
          submitting={create.isPending}
          onSubmit={(input) => create.mutate(input)}
          onCancel={() => setCreating(false)}
          error={create.error}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Links</CardTitle>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : links.length === 0 ? (
            <p className="text-sm text-muted-foreground">No special links yet.</p>
          ) : (
            <ul className="divide-y">
              {links.map((l) => (
                <li key={l.id} className="py-3">
                  {editing === l.id ? (
                    <SpecialLinkForm
                      link={l}
                      submitting={update.isPending}
                      onSubmit={(input) => update.mutate({ id: l.id, patch: input })}
                      onCancel={() => setEditing(null)}
                      error={update.error}
                    />
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{l.title}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground uppercase">
                            {l.type}
                          </span>
                          {!l.active && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              Hidden
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono truncate max-w-md">
                          {l.target}
                        </div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <Button variant="outline" size="sm" onClick={() => setEditing(l.id)}>
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => update.mutate({ id: l.id, patch: { active: !l.active } })}
                        >
                          {l.active ? 'Hide' : 'Show'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            if (confirm('Delete this link?')) remove.mutate(l.id);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SpecialLinkForm({
  link,
  submitting,
  onSubmit,
  onCancel,
  error,
}: {
  link?: SpecialLink;
  submitting: boolean;
  onSubmit: (input: SpecialLinkInput) => void;
  onCancel: () => void;
  error: unknown;
}) {
  const [title, setTitle] = useState(link?.title ?? '');
  const [type, setType] = useState<'zoom' | 'vimeo' | 'other'>(link?.type ?? 'zoom');
  const [target, setTarget] = useState(link?.target ?? '');
  const [sortOrder, setSortOrder] = useState<string>(String(link?.sortOrder ?? 0));
  const errMsg = (error as { response?: { data?: { error?: string } } } | null)?.response?.data
    ?.error;

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit({
      title: title.trim(),
      type,
      target: target.trim(),
      sortOrder: Number(sortOrder) || 0,
      active: link?.active ?? true,
    });
  }

  return (
    <form onSubmit={submit} className="rounded-md border bg-muted/30 p-4 space-y-3">
      <h3 className="text-base font-semibold">{link ? 'Edit link' : 'New link'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Weekly Zoom Training"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Type</label>
          <select
            className="w-full h-9 rounded-md border bg-background px-2 text-sm"
            value={type}
            onChange={(e) => setType(e.target.value as 'zoom' | 'vimeo' | 'other')}
          >
            <option value="zoom">Zoom</option>
            <option value="vimeo">Vimeo</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="space-y-1 md:col-span-2">
          <label className="text-xs text-muted-foreground">
            {type === 'vimeo'
              ? 'Vimeo URL or numeric id'
              : type === 'zoom'
                ? 'Zoom join URL'
                : 'Link URL'}
          </label>
          <Input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            required
            placeholder={
              type === 'vimeo' ? 'https://vimeo.com/123456789 or 123456789' : 'https://…'
            }
            className="font-mono text-xs"
          />
          <p className="text-[10px] text-muted-foreground">
            For Vimeo, set the video's privacy to “only play on your domain” so a copied embed can't
            be reshared. Zoom links are subscription-gated, but a determined member can still copy
            the URL — per-attendee Zoom links are a planned hardening.
          </p>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Sort order</label>
          <Input type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} />
        </div>
      </div>
      {errMsg && <p className="text-sm text-destructive">{errMsg}</p>}
      <div className="flex gap-2 pt-1">
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : link ? 'Save' : 'Create'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
